/**
 * Results Facet Container Component
 *
 * Container showing unified filters and brands with expandable sections.
 * Figma: Results Facet Container (Node: 51:6750)
 *
 * Two variants:
 * - Collapsed: Shows limited filters with expand option
 * - Expanded: Shows all filters (active and suggested) and brands in unified sections
 *
 * Pattern: Active items have remove buttons, suggested items have info icons
 */
"use client";

import { useMemo, useState } from "react";

import { colors } from "@/styles/design-tokens";

import { AppliedFiltersLabel } from "./AppliedFiltersLabel";
import { FacetButton } from "./FacetButton";
import { ScrollablePillRow } from "./ScrollablePillRow";

import type { FilterPill } from "./FilterSection";

interface ResultsFacetContainerProps {
  activeFilters: FilterPill[];
  suggestedFilters?: FilterPill[];
  selectedBrands?: FilterPill[];
  similarToBrands?: FilterPill[];
  totalFiltersCount?: number;
  totalBrandsCount?: number;
  className?: string;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Whether AI assistant is still loading recommendations */
  isLoadingAssistant?: boolean;
  onClearSuggestedFilters?: () => void;
  onClearSuggestedBrands?: () => void;
}

interface InlineClearFiltersButtonProps {
  label: string;
  onClick: () => void;
  className?: string;
}

function InlineClearFiltersButton({
  label,
  onClick,
  className = "",
}: InlineClearFiltersButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Match FacetButton pill height so the inline action aligns with neighboring chips.
      className={`inline-flex h-[24.49px] flex-shrink-0 items-center px-1 text-action-muted text-ink-secondary transition-colors hover:text-ink ${className}`}
    >
      {label}
    </button>
  );
}

export function ResultsFacetContainer({
  activeFilters,
  suggestedFilters = [],
  selectedBrands = [],
  similarToBrands = [],
  totalFiltersCount = 0,
  totalBrandsCount = 0,
  className = "",
  isExpanded: controlledExpanded,
  onExpandedChange,
  isLoadingAssistant = false,
  onClearSuggestedFilters,
  onClearSuggestedBrands,
}: ResultsFacetContainerProps) {
  const [internalExpanded, setInternalExpanded] = useState(true); // Default to expanded

  // Use controlled or uncontrolled state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  // Pre-filter active/suggested brands to avoid repeated .filter() calls
  const { activeBrands, suggestedBrandsList } = useMemo(
    () => ({
      activeBrands: selectedBrands.filter((b) => b.variant === "active"),
      suggestedBrandsList: selectedBrands.filter((b) => b.variant !== "active"),
    }),
    [selectedBrands]
  );

  const { activeSimilarBrands, suggestedSimilarBrands } = useMemo(
    () => ({
      activeSimilarBrands: similarToBrands.filter((b) => b.variant === "active"),
      suggestedSimilarBrands: similarToBrands.filter((b) => b.variant !== "active"),
    }),
    [similarToBrands]
  );

  // Calculate remaining items not displayed as pills in collapsed mode (for inline "+X more" pill).
  // In collapsed mode, we show at most 2 items each from: active filters, active brands, active similar brands.
  // The "more" count = (all active + all suggested items) - items actually displayed as pills.
  const moreCount = useMemo(() => {
    const displayedFilters = Math.min(activeFilters.length, 2);
    const displayedBrands = Math.min(activeBrands.length, 2);
    const displayedSimilar = Math.min(activeSimilarBrands.length, 2);
    const totalDisplayed = displayedFilters + displayedBrands + displayedSimilar;

    const totalActive = activeFilters.length + activeBrands.length + activeSimilarBrands.length;
    const totalSuggested =
      suggestedFilters.length + suggestedBrandsList.length + suggestedSimilarBrands.length;

    return totalActive + totalSuggested - totalDisplayed;
  }, [
    activeFilters.length,
    activeBrands.length,
    activeSimilarBrands.length,
    suggestedFilters.length,
    suggestedBrandsList.length,
    suggestedSimilarBrands.length,
  ]);

  const handleToggle = () => {
    const newValue = !isExpanded;
    if (onExpandedChange) {
      onExpandedChange(newValue);
    } else {
      setInternalExpanded(newValue);
    }
  };

  const showClearSuggestedFilters = isExpanded && !isLoadingAssistant && activeFilters.length > 0;
  const showClearSuggestedBrands = !isLoadingAssistant && activeBrands.length > 0;

  return (
    <div
      className={`max-w-full overflow-hidden transition-all duration-200 ease-out ${className}`}
      style={{
        backgroundColor: isExpanded ? colors.background.white : "transparent",
        border: isExpanded ? `0.9px solid #4A5565` : "none",
        borderRadius: isExpanded ? "8.75px" : "0",
        paddingLeft: isExpanded ? "14.9px" : "0",
        paddingRight: isExpanded ? "14.9px" : "0",
        paddingTop: "0px",
        paddingBottom: "0px",
      }}
    >
      {/* Unified Filters Section */}
      <div
        style={{
          paddingTop: "10.49px",
          paddingBottom: "10.49px",
          borderBottom: isExpanded ? `0.67px solid ${colors.divider.light}` : "none",
        }}
      >
        {/* Heading with expand/collapse */}
        <div
          style={{
            marginBottom: "6.99px",
          }}
        >
          <AppliedFiltersLabel
            label="SUGGESTED FILTERS"
            state={isExpanded ? "condensed" : "default"}
            filterCount={totalFiltersCount}
            brandCount={totalBrandsCount}
            moreCount={isExpanded ? 0 : moreCount}
            onExpandClick={handleToggle}
            mobileActionLabel={showClearSuggestedFilters ? "Clear filters" : undefined}
            onMobileActionClick={showClearSuggestedFilters ? onClearSuggestedFilters : undefined}
          />
        </div>

        {/* Filter Pills Row - Scrollable on mobile, wraps on desktop */}
        <ScrollablePillRow>
          {/* In collapsed mode, show limited filters */}
          {!isExpanded ? (
            <>
              {/* Show first few active filters */}
              {activeFilters.slice(0, 2).map((filter) => (
                <FacetButton
                  key={filter.id}
                  variant={filter.variant || "active"}
                  label={filter.label}
                  onRemove={filter.onRemove}
                  onInfoClick={filter.onInfoClick}
                  onClick={filter.onClick}
                  justification={filter.justification}
                />
              ))}

              {/* Show selected brands */}
              {activeBrands.slice(0, 2).map((brand) => (
                <FacetButton
                  key={`brand-${brand.id}`}
                  variant="active"
                  label={brand.label}
                  onRemove={brand.onRemove}
                  justification={brand.justification}
                  brandName={brand.label}
                />
              ))}

              {/* Show similar to brands with prefix */}
              {activeSimilarBrands.slice(0, 2).map((brand) => (
                <FacetButton
                  key={`similar-${brand.id}`}
                  variant="active"
                  label={`Similar To: ${brand.label}`}
                  onRemove={brand.onRemove}
                  justification={brand.justification}
                />
              ))}
            </>
          ) : (
            <>
              {/* In expanded mode, show all filters */}
              {/* Active filters first */}
              {activeFilters.map((filter) => (
                <FacetButton
                  key={filter.id}
                  variant={filter.variant || "active"}
                  label={filter.label}
                  onRemove={filter.onRemove}
                  onInfoClick={filter.onInfoClick}
                  onClick={filter.onClick}
                  justification={filter.justification}
                />
              ))}

              {/* Suggested filters after active ones */}
              {suggestedFilters.map((filter) => (
                <FacetButton
                  key={filter.id}
                  variant={filter.variant || "inactive-info"}
                  label={filter.label}
                  onInfoClick={filter.onInfoClick}
                  onClick={filter.onClick}
                  justification={filter.justification}
                />
              ))}

              {/* Skeleton pills while AI recommendations are loading */}
              {isLoadingAssistant && suggestedFilters.length === 0 && (
                <>
                  <FacetButton variant="loading" label="" />
                  <FacetButton variant="loading" label="" />
                  <FacetButton variant="loading" label="" />
                  <FacetButton variant="loading" label="" />
                </>
              )}

              {showClearSuggestedFilters && onClearSuggestedFilters && (
                <InlineClearFiltersButton
                  label="Clear filters"
                  onClick={onClearSuggestedFilters}
                  className="hidden lg:inline-flex"
                />
              )}
            </>
          )}
        </ScrollablePillRow>
      </div>

      {/* Selected Brands Section - only in expanded mode */}
      {isExpanded && selectedBrands.length > 0 && (
        <div
          style={{
            paddingTop: "10.49px",
            paddingBottom: "10.49px", // Match top padding for symmetry
          }}
        >
          {/* Heading */}
          <div
            style={{
              marginBottom: "6.99px",
            }}
          >
            <AppliedFiltersLabel
              label="SUGGESTED BRANDS"
              state="no-expand"
              mobileActionLabel={showClearSuggestedBrands ? "Clear brands" : undefined}
              onMobileActionClick={showClearSuggestedBrands ? onClearSuggestedBrands : undefined}
            />
          </div>

          {/* Brand filter pills - Scrollable on mobile */}
          <ScrollablePillRow>
            {selectedBrands.map((brand) => (
              <FacetButton
                key={brand.id}
                variant={brand.variant || "active"}
                label={brand.label}
                onRemove={brand.onRemove}
                onInfoClick={brand.onInfoClick}
                onClick={brand.onClick}
                justification={brand.justification}
                brandName={brand.label}
              />
            ))}

            {showClearSuggestedBrands && onClearSuggestedBrands && (
              <InlineClearFiltersButton
                label="Clear brands"
                onClick={onClearSuggestedBrands}
                className="hidden lg:inline-flex"
              />
            )}
          </ScrollablePillRow>
        </div>
      )}

      {/* Similar To Brands Section - only in expanded mode */}
      {isExpanded && similarToBrands.length > 0 && (
        <div
          style={{
            paddingTop: "10.49px",
            paddingBottom: "10.49px",
            borderTop: `0.67px solid ${colors.divider.light}`,
          }}
        >
          {/* Heading */}
          <div
            style={{
              marginBottom: "6.99px",
            }}
          >
            <AppliedFiltersLabel label="STYLE SIMILAR TO" state="no-expand" />
          </div>

          {/* Similar to brands filter pills - Scrollable on mobile */}
          <ScrollablePillRow>
            {similarToBrands.map((brand) => (
              <FacetButton
                key={brand.id}
                variant={brand.variant || "active"}
                label={brand.label}
                onRemove={brand.onRemove}
                onInfoClick={brand.onInfoClick}
                onClick={brand.onClick}
                justification={brand.justification}
                brandName={brand.variant === "inactive-info" ? brand.label : undefined}
              />
            ))}
          </ScrollablePillRow>
        </div>
      )}
    </div>
  );
}
